import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "./api";
import { AnalysisProvider, useAnalysis } from "./AnalysisContext";
import { AdminProvider } from "./AdminContext";
import { AuthProvider, useAuth } from "./AuthContext";
import Login from "./components/Login";
import Pricing from "./components/Pricing";
import Sidebar from "./components/Sidebar";
import League from "./components/League";
import BulkPredict from "./components/BulkPredict";
import History from "./components/History";
import Help from "./components/Help";
import Home from "./components/Home";
import Statistics from "./components/Statistics";
import Admin from "./components/Admin";
import "./App.css";



// İç bileşen — navigation state burada, queue state context'te
function AppInner() {
  const [weeklyStatus, setWeeklyStatus] = useState(null);
  const [userAnalysisEnabled, setUserAnalysisEnabled] = useState(true);

  // Public settings — analiz izni
  const fetchSettings = useCallback(() => {
    fetch(`${process.env.REACT_APP_API_URL || "https://web-production-c0e77.up.railway.app"}/api/public-settings`)
      .then(r => r.json())
      .then(d => setUserAnalysisEnabled(d.user_analysis_enabled ?? false))
      .catch(() => {});
  }, []);

  // Mount'ta çek
  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  const [statsTab, setStatsTab] = useState("istatistik");
  const [mode, setMode] = useState(
    window.location.hash === "#admin" ? "admin" : "home"
  );

  // Hash değişimini dinle — football.kalamar.com/#admin direkt açılsın
  useEffect(() => {
    const handleHash = () => {
      if (window.location.hash === "#admin") setMode("admin");
    };
    window.addEventListener("hashchange", handleHash);
    if (window.location.hash === "#admin") setMode("admin");
    return () => window.removeEventListener("hashchange", handleHash);
  }, []);

  // Mode değişince hash'i güncelle — admin dışında hash'i temizle
  useEffect(() => {
    if (mode === "admin") {
      window.history.replaceState(null, "", "#admin");
    } else {
      if (window.location.hash === "#admin") {
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
  }, [mode]);

  // Sayfa görünür olunca tahminleri yenile (tab'a geri dönünce)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshPredictions();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []); // eslint-disable-line

  // Haftalık analiz durumu polling
  const weeklyTimerRef = useRef(null);
  const startWeeklyPolling = useCallback(() => {
    const RAILWAY = "https://web-production-c0e77.up.railway.app";
    const poll = () => {
      fetch(`${RAILWAY}/api/weekly-status`)
        .then(r => r.json())
        .then(d => {
          setWeeklyStatus(d);
          if (d.running) {
            refreshPredictions();
            weeklyTimerRef.current = setTimeout(poll, 4000);
          } else {
            // Bitti — son kez yenile
            if (weeklyTimerRef.current) {
              refreshPredictions();
              weeklyTimerRef.current = null;
            }
          }
        })
        .catch(() => {});
    };
    if (weeklyTimerRef.current) clearTimeout(weeklyTimerRef.current);
    poll();
  }, []); // eslint-disable-line

  useEffect(() => {
    startWeeklyPolling();
    return () => { if (weeklyTimerRef.current) clearTimeout(weeklyTimerRef.current); };
  }, []); // eslint-disable-line
  const [selectedLeague, setSelectedLeague] = useState("Süper Lig");
  const [highlightPredId, setHighlightPredId] = useState(null);
  const DEFAULT_AI_KEY = "fa_default_ai";
  const [aiProvider, setAiProvider] = useState(() => localStorage.getItem(DEFAULT_AI_KEY) || "gpt");

  const { addToQueue, queue, clearDone, runningCount } = useAnalysis();
  const { isLoggedIn } = useAuth();
  const [predictions, setPredictions] = useState([]);

  const refreshPredictions = () => {
    // Herkese açık referans (Süper Lig) + kullanıcının açtığı diğer maçlar.
    // Public her zaman gelir (giriş gerektirmez); giriş varsa kullanıcının
    // unlock'ları üstüne bindirilir (aynı maç varsa kullanıcı kaydı kazanır).
    Promise.all([
      api.getPublicPredictions("").then(d => d.predictions || []).catch(() => []),
      api.getPredictions("").then(d => d.predictions || []).catch(() => []),
    ]).then(([pub, mine]) => {
      const byKey = {};
      for (const r of pub)  byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
      for (const r of mine) byKey[`${r.home}|${r.away}|${r.match_date}`] = r;
      setPredictions(Object.values(byKey));
    }).catch(() => {});
  };

  // Lig değişince tahminleri yükle
  useEffect(() => { refreshPredictions(); }, [selectedLeague]);
  // Giriş/çıkışta tahminleri tazele — artık kullanıcıya özel (açtıkları).
  useEffect(() => { refreshPredictions(); }, [isLoggedIn]); // eslint-disable-line

  // CTRL+ALT+A kısayolu
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        // Escape tuşu - gelecekte kullanılabilir
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handlePredictFromMatch = (match, league, existingPredictions) => {
    addToQueue([match], league, existingPredictions, aiProvider);
    setMode("predict");
  };

  const handleBulkPredict = (matches, league, existingPredictions) => {
    addToQueue(matches, league, existingPredictions, aiProvider);
    setMode("predict");
  };

  const handleViewPrediction = (prediction) => {
    setHighlightPredId(prediction.id);
    setMode("history");
  };

  const handleModeChange = (newMode) => {
    if (newMode !== "history") setHighlightPredId(null);
    setMode(newMode);
  };

  // display:none — gizli sayfalar DOM'da ama görünmez ve event almaz
  const hidden = { display: "none", pointerEvents: "none" };
  const visible = { display: "contents" };

  const [leagueSheetOpen, setLeagueSheetOpen] = useState(false);

  const MOBILE_NAV = [
    { id: "home",    icon: "🏠", label: "Ana Sayfa" },
    { id: "predict", icon: "🎯", label: "Tahmin" },
    { id: "history", icon: "📊", label: "Geçmiş" },
    { id: "leagues", icon: "⚽", label: "Ligler" },
    { id: "stats",   icon: "📈", label: "İstatistik" },
    { id: "help",    icon: "❓", label: "Yardım" },
  ];

  const LEAGUE_GROUPS_MOBILE = {
    "Türkiye":   ["Süper Lig"],
    "İngiltere": ["Premier League","Championship"],
    "İspanya":   ["La Liga","Segunda Division"],
    "Almanya":   ["Bundesliga","2. Bundesliga"],
    "İtalya":    ["Serie A","Serie B"],
    "Fransa":    ["Ligue 1","Ligue 2"],
    "Hollanda":  ["Eredivisie"],
    "Belçika":   ["Pro League"],
    "Portekiz":  ["Primeira Liga"],
    "Diğer":     ["World Cup","Champions League","Scottish Premiership","Austrian Bundesliga",
                  "Super League Greece","Russian Premier League","Superliga",
                  "Allsvenskan","Eliteserien","Swiss Super League"],
  }

  return (
    <div className="app">

      {/* Global analiz göstergesi — kaç maç analiz ediliyor, her sayfada görünür */}
      {runningCount > 0 && (
        <div style={{ position:"fixed", right:16, bottom:16, zIndex:900,
                      background:"#111827", color:"#fff", borderRadius:10, padding:"11px 16px",
                      boxShadow:"0 8px 24px rgba(0,0,0,.25)", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16, animation:"spin 1s linear infinite", display:"inline-block" }}>⏳</span>
          <span style={{ fontSize:13, fontWeight:600 }}>{runningCount} maç analiz ediliyor…</span>
        </div>
      )}

      <Sidebar
        selected={selectedLeague}
        onSelect={(league) => {
          if (league === null) {
            setSelectedLeague(null);
            setMode("predict");
          } else {
            setSelectedLeague(league);
            setMode("league");
          }
        }}
        mode={mode}
        onModeChange={handleModeChange}
        runningCount={runningCount}
        aiProvider={aiProvider}
        onChangeProvider={setAiProvider}
      />

      {/* Ana Sayfa — main/m-body dışında, flex row için */}
      {mode === "home" && (
        <Home
          onPredict={handlePredictFromMatch}
          onViewPrediction={handleViewPrediction}
          userAnalysisEnabled={userAnalysisEnabled}
          onKesfet={() => { setStatsTab("kalibrasyon"); setMode("stats"); }}
          onSorgula={() => { setStatsTab("sorgu"); setMode("stats"); }}
          onTahmin={() => setMode("predict")}
        />
      )}

      {/* Lig sayfası */}
      <League
        league={selectedLeague}
        active={mode === "league"}
        onPredict={handlePredictFromMatch}
        onBulkPredict={handleBulkPredict}
        onViewPrediction={handleViewPrediction}
        userAnalysisEnabled={userAnalysisEnabled}
      />

      {/* Haftalık Analiz Banner — devam ediyorsa göster */}
      {weeklyStatus?.running && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
          background: "#1e40af", color: "#fff",
          padding: "10px 20px", fontSize: 13, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}>
          <span style={{ fontSize: 16 }}>⏳</span>
          <span style={{ flex: 1 }}>
            <strong>Haftalık analiz devam ediyor</strong>
            {weeklyStatus.current_league && (
              <span style={{ opacity: 0.85 }}> — {weeklyStatus.current_league}</span>
            )}
            {weeklyStatus.current_match && (
              <span style={{ opacity: 0.7, fontSize: 12 }}> · {weeklyStatus.current_match}</span>
            )}
          </span>
          {weeklyStatus.total > 0 && (
            <div style={{
              background: "rgba(255,255,255,0.2)", borderRadius: 20,
              padding: "3px 12px", fontSize: 13, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span>{weeklyStatus.completed}</span>
              <span style={{ opacity: 0.6 }}>/</span>
              <span>{weeklyStatus.total}</span>
              <span style={{ opacity: 0.7, fontSize: 11 }}>maç</span>
              {weeklyStatus.total > 0 && (
                <div style={{
                  width: 60, height: 6, background: "rgba(255,255,255,0.2)",
                  borderRadius: 3, overflow: "hidden", marginLeft: 4,
                }}>
                  <div style={{
                    width: `${Math.round(weeklyStatus.completed / weeklyStatus.total * 100)}%`,
                    height: "100%", background: "#60a5fa", borderRadius: 3,
                    transition: "width 0.5s",
                  }} />
                </div>
              )}
            </div>
          )}
          {weeklyStatus.errors > 0 && (
            <span style={{ background: "#ef4444", borderRadius: 12,
              padding: "2px 8px", fontSize: 11 }}>
              {weeklyStatus.errors} hata
            </span>
          )}
        </div>
      )}

      {/* Tahmin Merkezi */}
      <div style={mode === "predict" ? visible : hidden} className="main">

        <div className="m-body">
          <BulkPredict
            queue={queue}
            onAdd={addToQueue}
            onClearDone={() => { clearDone(); refreshPredictions(); }}
            currentLeague={null}
            predictions={predictions}
            userAnalysisEnabled={userAnalysisEnabled}
          />
        </div>
      </div>

      {/* Geçmiş */}
      <div style={mode === "history" ? visible : hidden} className="main">
        <div className="m-body">
          <History highlightId={highlightPredId} active={mode === "history"} />
        </div>
      </div>

      {/* Yardım */}
      <div style={mode === "help" ? visible : hidden} className="main">
        <div className="m-body">
          <Help />
        </div>
      </div>

      {/* Admin Paneli — gizli, URL hash ile erişim */}
      <div style={mode === "admin" ? visible : hidden} className="main">
        <Admin onExit={() => setMode("home")} onAnalysisStart={startWeeklyPolling} onSettingsSaved={fetchSettings} />
      </div>

      {/* İstatistikler */}
      <div style={mode === "stats" ? visible : hidden} className="main">
        <div className="m-body">
          <Statistics initialTab={statsTab} />
        </div>
      </div>
      {/* Mobil alt navigasyon */}
      <nav className="mobile-nav">
        {MOBILE_NAV.map(item => (
          <button
            key={item.id}
            className={`mob-nav-item ${(item.id === "leagues" ? leagueSheetOpen : mode === item.id) ? "active" : ""}`}
            onClick={() => {
              if (item.id === "leagues") {
                setLeagueSheetOpen(o => !o);
              } else {
                setLeagueSheetOpen(false);
                handleModeChange(item.id);
              }
            }}
          >
            <span className="mob-icon">{item.icon}</span>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {/* Lig seçim sheet */}
      <div className={`mob-league-sheet-overlay ${leagueSheetOpen ? "open" : ""}`}
        onClick={() => setLeagueSheetOpen(false)}
        style={{ display: leagueSheetOpen ? "block" : "none" }} />
      <div className={`mob-league-sheet ${leagueSheetOpen ? "open" : ""}`}
        style={{ display: leagueSheetOpen ? "block" : "none" }}>
        <div className="mob-sheet-handle" />
        {Object.entries(LEAGUE_GROUPS_MOBILE).map(([country, leagues]) => (
          <div key={country}>
            <div style={{ fontSize: 10, color: "#999", textTransform: "uppercase",
              letterSpacing: ".07em", padding: "8px 8px 3px", fontWeight: 600 }}>
              {country}
            </div>
            {leagues.map(lg => (
              <button key={lg} onClick={() => {
                setSelectedLeague(lg);
                setMode("league");
                setLeagueSheetOpen(false);
              }} style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "9px 12px", fontSize: 14, fontWeight: 500,
                border: "none", background: selectedLeague === lg ? "#fff" : "transparent",
                borderRadius: 8, cursor: "pointer", color: "#333",
                borderLeft: selectedLeague === lg ? "3px solid #2563eb" : "none",
                marginBottom: 2,
              }}>{lg}</button>
            ))}
          </div>
        ))}
        <div style={{ height: 16 }} />
      </div>
    </div>
  );
}

// Dış bileşen — Provider'lar. AuthProvider en dışta: AnalysisContext token'a
// erişebilsin diye onun üstünde olmalı.
export default function App() {
  return (
    <AuthProvider>
      <AdminProvider>
        <AnalysisProvider>
          <AppInner />
          <Login />
          <Pricing />
        </AnalysisProvider>
      </AdminProvider>
    </AuthProvider>
  );
}
